// src/config/constants.js
// Hard-coded closed sets. Values that are used as DATABASE FILTERS are
// lowercase to match what your Supabase data actually stores (confirmed:
// bands.status = 'active', accounts.type = 'cash'/'bank'/'credit', etc.).
// Values used only as DISPLAY LABELS can be capitalised for readability —
// but anything compared against a DB column must match exactly.

import { i18nLabels } from './i18nLabels';

// ── Finance ──────────────────────────────────────────────────────────────
export const FINANCE_STATUS = ['Paid', 'Pending', 'Auto', 'Credit'];   // display + stored as-is

// ── Partners ─────────────────────────────────────────────────────────────
// A partner is either a Customer or a Supplier — never both.
export const PARTNER_TYPES = ['Customer', 'Supplier'];

// ── Accounts ─────────────────────────────────────────────────────────────
// DB filter values — must match accounts.type / accounts.classification exactly
export const ACCOUNT_TYPES = [
  'cash', 'bank', 'client', 'inventory',
  'supplier', 'loan',
  'revenue', 'expense', 'equity',
  'immobilisation',
];

export const ACCOUNT_CLASSIFICATION = [
  'asset', 'liability',
  'revenue', 'expense', 'equity',
];


// Display labels for account types (capitalised for UI)
export const ACCOUNT_TYPE_LABELS = i18nLabels('enums.accountType', {
  cash: 'Cash', bank: 'Bank', client: 'Client',
  supplier: 'Supplier', loan: 'Loan',
  revenue: 'Revenue', expense: 'Expense',
  inventory: 'Inventory', equity: 'Equity',
  immobilisation: 'Immobilisation',
  git: 'Goods In Transit',
});

// ── Items ─────────────────────────────────────────────────────────────────
// 454 (T-3): ITEM_CATEGORIES + ITEM_CATEGORY_LABELS are now DB-fed — hydrated in place from
// GET /api/category-constants by src/config/categoryConstants.js. Re-exported here so every existing
// importer is unchanged; a future 6th constant flows in without editing these.
export { ITEM_CATEGORIES, ITEM_CATEGORY_LABELS } from './categoryConstants';

// Revenue attribution options offered for FREE (non-egg, non-bird) categories. FIFO is deliberately NOT
// here — it is eggs-only (needs the egg production-layer trail) and shows as a locked value, never in
// this dropdown. eggs/birds render their locked value read-only (ItemsSettings).
export const REVENUE_ATTRIBUTION = [
  'Unallocated', 'By_Eggs', 'Direct', 'By_Birds',
];

export const REVENUE_ATTRIBUTION_LABELS = i18nLabels('enums.attribution', {
  Unallocated: 'Unallocated', By_Eggs: 'By Eggs', Direct: 'Direct', By_Birds: 'By Birds', FIFO: 'FIFO',
});

// ── Bands ────────────────────────────────────────────────────────────────
// DB filter values — confirmed lowercase in your data
export const BAND_PHASE   = ['not_started', 'growing', 'laying', 'retired']; // v_band_lifecycle_state.phase
// 603 B6 A5: deleted the dead `BAND_STATUS = ['active','inactive']` const (0 importers; its name collided with the
// commitment column band_status {queued/active/released/cancelled}). Removed to end the documentation trap.

export const PHASE_LABEL = i18nLabels('bandPhase', {
  not_started: 'Not Started', growing: 'Growing', laying: 'Laying', retired: 'Retired',
});

export const PHASE_COLORS = {
  not_started: '#94a3b8',
  growing:     '#2563eb',
  laying:      '#16a34a',
  retired:     '#64748b',
};

export const STATUS_LABEL = i18nLabels('enums.status', { active: 'Active', inactive: 'Inactive' });

// ── Stock ────────────────────────────────────────────────────────────────
export const STOCK_MOVEMENT_TYPES = [
  'Purchase', 'Consumption', 'Mortality', 'Transfer',
  'Production', 'Sale', 'Adjustment', 'Opening',
];

export const MOVEMENT_TYPE_LABELS = i18nLabels('enums.movementType', {
  sale: 'Sale', purchase: 'Purchase', transfer: 'Transfer', mortality: 'Mortality',
  production: 'Production', consumption: 'Consumption', adjustment: 'Adjustment', opening: 'Opening',
});

// ── Storage ──────────────────────────────────────────────────────────────
// Storage-type collapse: the STORAGE_TYPES / STORAGE_TYPES_USER / STORAGE_TYPE_LABELS enum was removed.
// A storage is typed to a CONSTANT (storages.constant_key); the storage admin form picks a constant
// from GET /api/storage-types (which now serves the constants), and pickers filter by constant_key.

// ── Finance SubTypes — "Applies To" dropdown options ─────────────────────
export const FINANCE_APPLIES_TO = ['Expense', 'Sale', 'Purchase', 'Financial', 'All'];

// ── Operation Types — "Target" dropdown options ───────────────────────────
export const OPERATION_TARGETS = [
  'finance', 'sales+finance', 'purchase_detail+finance',
  'production', 'consumption', 'mortality', 'stock_entry',
];

export const OPERATION_TARGET_LABELS = i18nLabels('enums.operationTarget', {
  finance: 'Finance',
  'sales+finance': 'Sales + Finance',
  'purchase_detail+finance': 'Purchase + Finance',
  production: 'Production',
  consumption: 'Consumption',
  mortality: 'Mortality',
  stock_entry: 'Stock Entry',
});

export const TARGET_TO_DOMAIN = {
  'purchase_detail+finance': 'procurement',
  'sales+finance': 'sales',
  finance: 'finance',
  production: 'production',
  consumption: 'production',
  mortality: 'production',
  stock_entry: 'stock',
};

export const DOMAIN_LABELS = i18nLabels('enums.domain', {
  procurement: 'Procurement',
  sales: 'Sales',
  finance: 'Finance',
  production: 'Production',
  stock: 'Stock',
});

// ── Account Classification ────────────────────────────────────────────────
export const CLASSIFICATION_LABELS = i18nLabels('enums.classification', {
  asset: 'Asset', liability: 'Liability', revenue: 'Revenue', expense: 'Expense', equity: 'Equity',
});

// ── Operation groups (for Data Entry tab selectors) ───────────────────────
export const STOCK_MOVEMENT_OPS  = ['Transfer', 'Adjustment', 'Opening'];
export const LOAN_OPS            = ['Loan_In', 'Loan_Out'];
export const FINANCE_OPS         = ['Payment', 'Loan_In', 'Loan_Out', 'Financial Operation'];

// ── Cost classification / scope ───────────────────────────────────────────
export const COST_CLASS_LABELS = i18nLabels('enums.costClass', { variable: 'Variable', fixed: 'Fixed', none: 'Excluded' });
export const SCOPE_LABELS      = i18nLabels('enums.scope', { band: 'Band', farm: 'Farm', location: 'Location', global: 'Global' });
